import type {
  AcademyOperatingProfileInput,
  ClubOperatingProfileInput,
  OrgOperationType,
} from '@court-sync/shared';
import type { OnboardingDraft } from './onboardingDraft';
import {
  resolveQuestionnaireWizardSteps,
  type QuestionnaireStepId,
} from '../components/onboarding/AcademyNeedsQuestionnaire';
import { CLUB_QUESTIONNAIRE_WIZARD_STEPS, resolveClubQuestionnaireWizardSteps } from '../components/onboarding/ClubNeedsQuestionnaire';

export const OPERATION_TYPE_STEP = {
  id: 'operation-type',
  title: 'What are you setting up?',
  description: 'Choose academy programs, club facilities, or both.',
} as const;

export const ACADEMY_DETAILS_STEP = {
  id: 'academy-details',
  title: 'Academy details',
  description: 'Add your academy name, location, and timezone.',
} as const;

export const CLUB_DETAILS_STEP = {
  id: 'club-details',
  title: 'Club details',
  description: 'Add your club name, country, and timezone.',
} as const;

export const BOTH_PLANS_STEP = {
  id: 'both-plans',
  title: 'Choose your plans',
  description: 'Select academy and club subscription plans for your organization.',
} as const;

export type SetupStepId =
  | typeof OPERATION_TYPE_STEP.id
  | typeof ACADEMY_DETAILS_STEP.id
  | typeof CLUB_DETAILS_STEP.id
  | typeof BOTH_PLANS_STEP.id
  | QuestionnaireStepId
  | (typeof CLUB_QUESTIONNAIRE_WIZARD_STEPS)[number]['id'];

export interface SetupWizardStep {
  id: SetupStepId;
  title: string;
  description: string;
}

export function buildSetupSteps(
  operationType: OrgOperationType | undefined,
  academyProfile: Partial<AcademyOperatingProfileInput>,
  _clubProfile: Partial<ClubOperatingProfileInput>
): SetupWizardStep[] {
  const op = operationType ?? 'academy';

  if (op === 'club') {
    const clubQuestionnaire = resolveClubQuestionnaireWizardSteps();
    const clubSteps = clubQuestionnaire.filter((s) => s.id !== 'plan');
    const clubPlanStep = clubQuestionnaire.find((s) => s.id === 'plan');
    return [
      OPERATION_TYPE_STEP,
      ...clubSteps,
      CLUB_DETAILS_STEP,
      ...(clubPlanStep ? [clubPlanStep] : []),
    ];
  }

  const academyQuestionnaire = resolveQuestionnaireWizardSteps(academyProfile);
  const academySteps = academyQuestionnaire.filter((s) => s.id !== 'plan');

  if (op === 'academy') {
    return [
      OPERATION_TYPE_STEP,
      ...academySteps,
      ACADEMY_DETAILS_STEP,
      academyQuestionnaire.find((s) => s.id === 'plan')!,
    ];
  }

  const clubSteps = resolveClubQuestionnaireWizardSteps({
    skipSports: true,
    skipLocations: true,
  }).filter((s) => s.id !== 'plan');
  return [
    OPERATION_TYPE_STEP,
    ...academySteps,
    ...clubSteps,
    {
      id: ACADEMY_DETAILS_STEP.id,
      title: 'Organization details',
      description: 'Add your academy and club names, location, and timezone.',
    },
    BOTH_PLANS_STEP,
  ];
}

export function buildSetupStepsFromDraft(draft: OnboardingDraft): SetupWizardStep[] {
  if (draft.pricingFastTrack) {
    return buildFastTrackSetupSteps(draft.orgOperationType ?? 'academy');
  }
  return buildSetupSteps(
    draft.orgOperationType,
    draft.operatingProfile,
    draft.clubOperatingProfile ?? {},
  );
}

export function buildFastTrackSetupSteps(operationType: OrgOperationType): SetupWizardStep[] {
  if (operationType === 'club') {
    return [CLUB_DETAILS_STEP];
  }
  if (operationType === 'both') {
    return [
      {
        id: ACADEMY_DETAILS_STEP.id,
        title: 'Organization details',
        description: 'Add your academy and club names, location, sports, and timezone.',
      },
    ];
  }
  return [ACADEMY_DETAILS_STEP];
}

export function hasOrgEntityFromUser(user: {
  academyId?: string | null;
  roles?: string[];
  rolesWithAcademy?: Array<{ role: string; clubId?: string | null }>;
}): boolean {
  if (user.academyId) return true;
  const roles = user.roles ?? [];
  if (roles.includes('club_owner') || roles.includes('club_admin')) return true;
  return (
    user.rolesWithAcademy?.some(
      (r) => (r.role === 'club_owner' || r.role === 'club_admin') && r.clubId != null
    ) ?? false
  );
}

export function isClubOnlyUser(user: {
  academyId?: string | null;
  roles?: string[];
}): boolean {
  const isClubOwner = user.roles?.includes('club_owner') ?? false;
  return isClubOwner && !user.academyId;
}
